import {useMemo} from 'react'

import {removeAccents} from '@s-ui/js/lib/string'

import {DEFAULT_EMPTY_TOTAL_RESULTS} from './config.js'

const getIsIncluded = ({label, searchByText}) =>
  removeAccents(String(label))
    .toUpperCase()
    .includes(removeAccents(String(searchByText)).toUpperCase())

const getFilteredItemsBySearch = ({items, searchByText}) =>
  items.filter(({label}) => getIsIncluded({label, searchByText}))

const getItemTotalResults = ({aggregations, itemId}) =>
  aggregations.find(({id}) => String(id) === String(itemId))?.totalResults || DEFAULT_EMPTY_TOTAL_RESULTS

const getTransformedProvincesByCCAA = ({aggregations, i18n, provincesByCCAA}) => {
  const hasAggregations = Boolean(aggregations)

  const transformedProvincesByCCAA = provincesByCCAA.reduce((acc, curr) => {
    const updatedObj = {...curr}

    updatedObj.nestedItems = updatedObj.items
    delete updatedObj.items

    updatedObj.nestedItems = updatedObj.nestedItems.map(item => ({
      ...item,
      ...(hasAggregations &&
        item.id && {
          extendedInfo: i18n.n(Number(getItemTotalResults({aggregations, itemId: item.id})))
        })
    }))
    acc.push(updatedObj)

    return acc
  }, [])

  return transformedProvincesByCCAA
}

export const useGetCurrentOptions = ({aggregations, i18n, provincesByCCAA, searchByText}) => {
  const transformedProvincedByCCAA = useMemo(
    () => getTransformedProvincesByCCAA({aggregations, i18n, provincesByCCAA}),
    [aggregations, i18n, provincesByCCAA]
  )
  // Returns options where the group item or a nestedItem matches the search
  const filteredOptionsBySearch = transformedProvincedByCCAA.filter(
    ({label, nestedItems = []}) =>
      getIsIncluded({label, searchByText}) ||
      nestedItems.some(({label: nestedItemLabel}) => getIsIncluded({label: nestedItemLabel, searchByText}))
  )
  // Returns only the nestedItems matching the search
  return filteredOptionsBySearch.map(({id, nestedItems = [], ...rest}) => {
    const filteredItemsByText = getFilteredItemsBySearch({items: nestedItems, searchByText})
    return {
      ...rest,
      id,
      isDisabled: Boolean(filteredItemsByText.length) && filteredItemsByText.length !== nestedItems.length,
      nestedItems: filteredItemsByText.length ? filteredItemsByText : nestedItems
    }
  })
}

export const getProvincesNameById = ({availableProvinces, selectedProvinces}) => {
  const selected = new Set(selectedProvinces.map(id => String(id)))

  return availableProvinces
    .map(ccaa => {
      const provinceIds = ccaa.items.map(province => String(province.id))
      return provinceIds.every(provinceId => selected.has(provinceId))
        ? ccaa.label
        : ccaa.items
            .filter(province => selected.has(String(province.id)))
            .map(province => province.label)
            .join(', ')
    })
    .filter(name => name)
    .join(', ')
}
